export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function isAndroid() {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent);
}

export function isMobileDevice() {
  return isIOS() || isAndroid();
}

export function isContactPickerSupported() {
  return (
    typeof navigator !== 'undefined' &&
    'contacts' in navigator &&
    typeof navigator.contacts?.select === 'function'
  );
}

export function normalizePhoneForSms(raw) {
  const value = String(raw || '').trim();
  if (!value) return '';

  if (value.startsWith('+')) {
    const digits = value.slice(1).replace(/\D/g, '');
    return digits ? `+${digits}` : '';
  }

  const digits = value.replace(/\D/g, '');
  return digits || '';
}

function formatAddressForPlatform(raw) {
  const normalized = normalizePhoneForSms(raw);
  if (!normalized) return '';

  if (isIOS()) {
    return normalized.startsWith('+') ? normalized.slice(1) : normalized;
  }

  return normalized;
}

export function buildSmsUri(phones, body) {
  const message = String(body || '');
  const addresses = [...new Set(phones.map(formatAddressForPlatform).filter(Boolean))];
  const encodedBody = encodeURIComponent(message);

  if (addresses.length === 0) {
    return isIOS() ? `sms:&body=${encodedBody}` : `sms:?body=${encodedBody}`;
  }

  const joined = addresses.join(',');

  if (isIOS()) {
    return `sms:/open?addresses=${encodeURIComponent(joined)}&body=${encodedBody}`;
  }

  return `sms:${joined}?body=${encodedBody}`;
}

export function openSmsUri(uri) {
  const anchor = document.createElement('a');
  anchor.href = uri;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

async function pickContactPhones() {
  const contacts = await navigator.contacts.select(['tel'], { multiple: true });
  const phones = contacts.flatMap((contact) =>
    (contact.tel || []).map((entry) => String(entry))
  );

  return [...new Set(phones.map(normalizePhoneForSms).filter(Boolean))];
}

export async function inviteContactsViaSms(message) {
  const text = String(message || '').trim();
  if (!text) {
    throw new Error('EMPTY_MESSAGE');
  }

  if (isContactPickerSupported()) {
    let phones = [];

    try {
      phones = await pickContactPhones();
    } catch (error) {
      if (error?.name === 'NotAllowedError' || error?.name === 'AbortError') {
        throw new Error('CANCELLED');
      }
      throw error;
    }

    if (phones.length === 0) {
      throw new Error('CANCELLED');
    }

    openSmsUri(buildSmsUri(phones, text));
    return { method: 'contact-picker-sms', count: phones.length };
  }

  if (isMobileDevice()) {
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ text });
        return { method: 'web-share' };
      } catch (error) {
        if (error?.name === 'AbortError') {
          throw new Error('CANCELLED');
        }
      }
    }

    openSmsUri(buildSmsUri([], text));
    return { method: 'sms-compose' };
  }

  throw new Error('DESKTOP_UNSUPPORTED');
}

function contactDisplayName(contact) {
  const raw = contact?.name;
  if (Array.isArray(raw)) {
    return String(raw[0] || '').trim();
  }
  return String(raw || '').trim();
}

export async function pickContactsForImport({ multiple = true } = {}) {
  if (typeof navigator === 'undefined' || typeof navigator.contacts?.select !== 'function') {
    throw new Error('UNSUPPORTED');
  }

  let contacts = [];
  try {
    contacts = await navigator.contacts.select(['name', 'tel', 'email'], { multiple });
  } catch (error) {
    if (error?.name === 'NotAllowedError' || error?.name === 'AbortError') {
      throw new Error('CANCELLED');
    }
    throw error;
  }

  return mapPickerContactsToRows(contacts);
}

function mapPickerContactsToRows(contacts) {
  const rows = [];
  for (const contact of contacts) {
    const displayName = contactDisplayName(contact) || 'Contact';
    const phones = contact.tel || [];
    const emails = contact.email || [];
    const email = emails[0] ? String(emails[0]).trim() : null;

    if (!phones.length) {
      continue;
    }

    for (const tel of phones) {
      const phone = normalizePhoneForSms(String(tel));
      if (!phone) continue;
      rows.push({ name: displayName, phone, email: email || undefined });
    }
  }

  return rows;
}

/** Try the native contact picker; falls back with UNSUPPORTED when unavailable. */
export async function tryPickContactsFromDevice({ multiple = true } = {}) {
  return pickContactsForImport({ multiple });
}

export function openSingleContactSms(phone, body) {
  const normalized = normalizePhoneForSms(phone);
  if (!normalized) {
    throw new Error('NO_PHONE');
  }
  openSmsUri(buildSmsUri([normalized], body));
}
